import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserAccountDTO, UserLoginDataDTO, UsersAccountDTO, UserRegisterDataDTO, TravelCountry, CallMeBackTravel } from '../classes/user-account.dto';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  AllergenRecordPayload,
  DeleteScheduleRequest,
  DoctorData,
  DoctorDetails,
  DrugPayload,
  MedicalRecordMenuInfo,
  PathologyRecordPayload,
  SurgicalRecordPayload,
  VaccinationPayload,
} from '../interfaces/medical-history.interface';
import { UpdatePreferencesDTO, RedeemCodeDTO, ScheduleDTO, MeasurementDTO } from '../classes/api-dto';
import { IChangePassword } from '../interfaces/change-password.interface';
import { MedicalMisurationType } from '../components/misuration/misuration.component';
import { DataService } from './data.service';
import { IInitializePayment } from '../interfaces/payment.interface';
import { ConfigService } from './env-config.service';
import { AppointmentsPayload } from '../interfaces/appointments-payload.interface';
import { SsoConfiguration } from '../interfaces/sso-configuration.interface';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private dataService: DataService,
    private config: ConfigService
  ) {}

  /**
   * Return headers
   * @returns {HttpHeaders}
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'text/plain' });
  }

  /**
   * Login user
   * @param {UserLoginDataDTO} loginData
   * @returns {Observable<UserAccountDTO>}
   */
  public login(loginData: UserLoginDataDTO): Observable<UserAccountDTO> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<UserAccountDTO>(this.config.baseUrl + '/api/UserAccount/login', loginData, { headers });
  }

  public resetPassword(email: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = {
      Username: email,
    };
    return this.http.post<any>(this.config.baseUrl + '/api/Account/resetpassword', body, { headers });
  }

  /**
   * Get all profiles by user
   * @param {UserAccountDTO} activeUser
   * @returns {Observable<UsersAccountDTO>}
   */
  public getUsers(activeUser: UserAccountDTO): Observable<UsersAccountDTO> {
    const headers: HttpHeaders = this.getHeaders();
    const body = {
      individualId: activeUser.individualId,
    };
    return this.http.post<UsersAccountDTO>(
      this.config.baseUrl + `/api/user/connectedprofiles`,
      body,
      { headers }
    );
  }

  public getUserProfileFromKeycloak(): Observable<UserAccountDTO> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<UserAccountDTO>(this.config.baseUrl + `/api/UserAccount/Profile`, { headers });
  }

  public getCompanyIdsList(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/UserAccount/companyidslist`, { headers });
  }

  public getTravelCountries(lang: string): Observable<TravelCountry[]> {
    const headers = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/DomainData/TravelCountries/${lang}`, { headers });
  }

  // TODO: add comment
  public canSendRequest(consultType: string, body: any, type: string): Observable<Object> {
    const headers: HttpHeaders = this.getHeaders();
    if (type === 'profile-selection-care-manager') {
      return this.http.post(this.config.baseUrl + `/api/Consult/cansendrequest`, body, { headers });
    } else {
      return this.http.post(this.config.baseUrl + `/api/Consult/cansendrequestConsulting/${consultType}`, body, { headers });
    }
  }

  // TODO: add comment
  public specialistByLanguage(): Observable<any> {
    /* TODO: appena l'api risponde tipizzare */
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(
      this.config.baseUrl + `/api/Consult/specialistsbylanguage/${this.translateService.currentLang[0].toUpperCase() + this.translateService.currentLang.slice(1)}`,
      { headers }
    );
  }

  /**
   * Get all medical information for the current user
   * @param {string} individualId
   * @returns {Observable<any>}
   */
  medicalProfileData(individualId: string): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post<any>(
      this.config.baseUrl + `/api/user/medicalprofiledataextended`,
      { individualId },
      { headers }
    );
  }

  /**
   * Get medical stats for one user
   * @param {string} individualId  TODO
   * @returns {Observable<any>}
   */
  public mainMedicalStats(individualId: string, birthDate: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/MedicalStats/mainmedicalstatsmultilanguage/${birthDate}`,
      payload,
      { headers }
    );
  }

  /**
   * Get medical specific stat
   * @param {string} individualId TODO
   * @returns {Observable<any>}
   */
  specificMedicalStat(specificType: string, individualId: string, fromDate: string, toDate: string): Observable<any> {
    /* TODO Tipizzare quando funzionerà l'API, terminare l'inserimento dei parametri */
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      individualId: individualId,
    };
    return this.http.post(this.config.baseUrl + `/api/MedicalStats/${specificType}/${fromDate}/${toDate}/1/1000`, payload, { headers });
  }

  /**
   * Update user medical data
   * @param {MeasurementDTO} body
   * @returns {Observable<MeasurementDTO>}
   */
  updateMedicalProfileData(body: MeasurementDTO): Observable<MeasurementDTO> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/User/updatemedicalprofiledata`, body, { headers });
  }

  /**
   * Get medical passport in html view
   * @param {string} individualId
   * @param {string} language
   * @param {'EA'} source
   * @returns {Observable<any>}
   */
  public getMedicalPassport(individualId: string, language: string, source: string = 'EA'): Observable<string> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post(
      this.config.baseUrl + `/api/MedicalRecord/medicalpassporthtmlviewMasked/${language}/${source}`,
      payload,
      {
        headers,
         responseType: 'text'
      },
    );
  }

  getMedicalRecordsOptionsByKey(key: string): Observable<any[]> {
    const headers: HttpHeaders = this.getHeaders();
    const language = this.translateService.currentLang;
    const endpoints: { [key: string]: string } = {
      pathology: this.config.baseUrl + `/api/medicalrecord/pathologyItemsByLanguage/${language}`,
      vaccination: this.config.baseUrl + `/api/medicalrecord/vaccineItemsByLanguage/${language}`,
      drug: this.config.baseUrl + `/api/medicalrecord/activesubstanceItemsbyLanguage/${language}`,
      surgical: this.config.baseUrl + `/api/medicalrecord/clinicalInterventionBylanguage/${language}`,
      allergen: this.config.baseUrl + `/api/medicalrecord/allergenFromTranslation/${language}`,
    };
    const url = endpoints[key];
    return this.http.get<any[]>(url, { headers });
  }

  getDrugFormat(drugName: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const language = this.translateService.currentLang;
    return this.http.get<any>(this.config.baseUrl + `/api/medicalrecord/getFormat/${drugName}/${language}`, { headers });
  }

  getRelatedData(type: 'pathology' | 'surgical', code: string): Observable<{ type: string; value: string }[]> {
    const headers: HttpHeaders = this.getHeaders();
    const language = this.translateService.currentLang;
    const endpoint =
      type === 'pathology'
        ? `/api/medicalrecord/pathologyMedicalReletedDataItemsBylanguage/${code}/${language}`
        : `/api/medicalrecord/clinicalInterventionFilteredDataBylanguage/${code}/${language}`;

    return this.http.get<any[]>(this.config.baseUrl + endpoint, { headers }).pipe(
      map((response) =>
        response.map((item) => ({
          type: item.type,
          value: item.translation?.value || 'N/A',
        }))
      )
    );
  }

  getSpecificAllergen(allergenGroupCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const language = this.translateService.currentLang;
    return this.http.get<any>(this.config.baseUrl + `/api/medicalrecord/specificallergenItemsByLanguage/${allergenGroupCode}/${language}`, { headers });
  }

  public deleteDocument(individualId: string, docid: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalrecord/snapshotanddeletedocument/${docid}`,
      payload,
      { headers }
    );
  }

  public downloadSingleDocument(individualId: string, docName: string, toDownload: boolean = false): Observable<{ docUrl: string }> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<{ docUrl: string }>(
      this.config.baseUrl + `/api/medicalrecord/documenturl/${docName}/${toDownload}`,
      payload,
      { headers }
    );
  }

  public getDoctorDetails(individualId: string): Observable<DoctorDetails> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<DoctorDetails>( this.config.baseUrl + `/api/medicalrecord/getdoctor`, payload, { headers });
  }

  addPathologyRecord(individualId: string, payload: PathologyRecordPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId}
    return this.http.post<any>(this.config.baseUrl + `/api/medicalrecord/pathology`, body, { headers });
  }

  addSurgicalRecord(individualId: string, payload: SurgicalRecordPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId};
    return this.http.post<any>(this.config.baseUrl + `/api/medicalrecord/surgical`, body, { headers });
  }

  addAllergenRecord(individualId: string, payload: AllergenRecordPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = {...payload, individualId};
    return this.http.post<any>(this.config.baseUrl + `/api/medicalrecord/allergen`, body, { headers });
  }

  public addDrugRecord(individualId: string, payload: DrugPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalrecord/insertdrug`,
      body,
      { headers }
    );
  }

  addVaccinationRecord(individualId: string, payload: VaccinationPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId};
    return this.http.post<any>(this.config.baseUrl + `/api/medicalrecord/vaccination`, body, { headers });
  }

  updatePathologyRecord(individualId: string, payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId}
    return this.http.put<any>(this.config.baseUrl + `/api/medicalrecord/pathology`, body, { headers });
  }

  updateSurgicalRecord(individualId: string, payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId};
    return this.http.put<any>(this.config.baseUrl + `/api/medicalrecord/surgical`, body, { headers });
  }

  updateAllergenRecord(individualId: string, payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = {...payload, individualId};
    return this.http.put<any>(this.config.baseUrl + `/api/medicalrecord/allergen`, body, { headers });
  }

  updateDrugRecord(individualId: string, payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId};
    return this.http.put<any>(this.config.baseUrl + `/api/medicalrecord/updatedrug`, body, { headers });
  }

  updateVaccinationRecord(individualId: string, payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...payload, individualId};
    return this.http.put<any>(this.config.baseUrl + `/api/medicalrecord/vaccination`, body, { headers });
  }

  /*
   deleteNotificationSchedule(payload: DeleteScheduleRequest): Observable<any> {
     const headers: HttpHeaders = this.getHeaders();
     return this.http.post<any>(`/api/notification/deleteSchedule`, payload, {headers});
   }
*/
  public saveDoctorDetails(individualId: string, doctorData: DoctorData): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { ...doctorData, individualId };
    return this.http.post<any>(this.config.baseUrl + `/api/medicalrecord/createdoctor`, body, { headers });
  }

  public getMedicalRecordMenuInfo(individualId: string): Observable<MedicalRecordMenuInfo> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<MedicalRecordMenuInfo>(
      this.config.baseUrl + `/api/medicalrecord/getMedicalrecordmenuinfo`,
      payload,
      { headers }
    );
  }

  public deleteMedicalRecord(key: string, individualId: string, record: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const endpoints: { [key: string]: string } = {
      pathology: `/api/medicalrecord/deletepathology`,
      surgical: `/api/medicalrecord/deletesurgical`,
      allergen: `/api/medicalrecord/deleteallergen`,
      drug: `/api/medicalrecord/deletedrug`,
      vaccination: `/api/medicalrecord/deletevaccination`,
    };
    const endpoint = endpoints[key];
    const body = { ...record, individualId };

    return this.http.post<any>(this.config.baseUrl + endpoint, body, { headers });
  }


  //MOCK CHIAMATE

  /*addSurgicalRecord(individualId: string, payload: SurgicalRecordPayload): Observable<any> {
    return of({ success: true, message: 'Surgical record added successfully' });
  }

  addAllergenRecord(individualId: string, payload: AllergenRecordPayload): Observable<any> {
    return of({ success: true, message: 'Allergen record added successfully' });
  }

  addPathologyRecord(individualId: string, payload: PathologyRecordPayload): Observable<any> {
    return of({ success: true, message: 'Pathology record added successfully' });
  }*/

  deleteNotificationSchedule(payload: DeleteScheduleRequest): Observable<any> {
    return of({ success: true, message: 'Notification schedule deleted successfully' });
  }

  public getCalendarDates(body: any, calendarUrl: string): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/plain',
      'x-app': '3'
    });
    console.log(calendarUrl);
    return this.http.post<any>(calendarUrl + 'Mission/GetCalendarSchedulesByParams', body, { headers });
  }

  public getCalendarDatesByModify(body: any, calendarUrl: string): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/plain',
      'x-app': '3',
    });
    return this.http.post<any>(calendarUrl + 'Mission/GetCalendarSchedulesByDossierCode', body, { headers });
  }

  public scheduleAppointment( body: any, individualId: { currentContactId: string; individualId: string }, languageCode: string,
  type: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    body.currentContactId = individualId.currentContactId;
    body.individualId = individualId.individualId;
    body.languageCode = languageCode;
    switch (type) {
      case 'GenericPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/phonegenericByIndividualId`, body, { headers });
      case 'GenericVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videogenericByIndividualId`, body, { headers });
      case 'SpecialistPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/phonespecialistByIndividualId`, body, { headers });
      case 'SpecialistVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videospecialistByIndividualId`, body, { headers });
      case 'GenericTelevisitVideo':
      case 'GenericTelevisitVideoPayment':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videogenericTeleVisitByIndividualId`, body, { headers });
      case 'SpecialistTelevisitVideo':
      case 'SpecialistTelevisitVideoPayment':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videospecialistTeleVisitByIndividualId`, body, { headers });
      case 'VeterinarianPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/phoneveterinarianByIndividualId`, body, { headers });
      case 'VeterinarianVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videoveterinarianByIndividualId`, body, { headers });
      case 'Psicologo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/treatmentPathPsiTelevisitFuByIndividualId`, body, { headers });
      case 'Nutrizionista':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/treatmentPathNutTelevisitFuByIndividualId`, body, { headers });
      case 'PsychologistVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videopsychologistByIndividualId`, body, { headers });
      case 'TreatmentPathNutTelevisit':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/treatmentPathNutTelevisitByIndividualId`, body, { headers });
      case 'TreatmentPathPsiTelevisit':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/treatmentPathPsiTelevisitByIndividualId`, body, { headers });
      case 'PsychologistPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/phonepsychologistByIndividualId`, body, { headers });
      case 'CareManager':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/caremanager`, body, { headers });
      default:
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/videospecialistByIndividualId`, body, { headers });
    }
  }

  public sendApinotification(body: any) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/notification/send`, body, { headers });
  }

  public getNotificationCard(service: string, individualId: string) {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { service, individualId };
    return this.http.post<any>( this.config.baseUrl + `/api/Consult/countersByIndividualId`, payload, { headers });
  }

  checkcodevalidity(activationCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const isOnMobile = localStorage.getItem('OnMobile') === 'true';
    let companyId = this.dataService.getCompanyId();
    if (isOnMobile) {
      if (companyId !== '45C41017' // FASDAC company ID
        && companyId !== '45C41377' // A2A company ID
      ) {
        companyId = '';
      }
    }
    return this.http.get(this.config.baseUrl + `/api/Redeem/checkcodevalidity?activationCode=${activationCode}&companyId=${companyId}`, { headers });
  }

  public isPolicyHolder(email: string, fiscalCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { email, taxCode: fiscalCode };
    return this.http.post<any>(
      this.config.baseUrl + `/api/redeem/isPolicyHolder`,
      payload,
      { headers }
    );
  }

  public getIndividualId(email: string, taxCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { email, taxCode };
    return this.http.post<any>(
      this.config.baseUrl + `/api/redeem/getindividualid`,
      payload,
      { headers }
    );
  }

  getCountries(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders().set('noLoader', 'true');
    return this.http.get(this.config.baseUrl + `/api/DomainData/Countries`, { headers });
  }

  getDistrict(countryCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders().set('noLoader', 'true');
    return this.http.get(this.config.baseUrl + `/api/DomainData/District/${countryCode}`, { headers });
  }

  getProvince(province: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders().set('noLoader', 'true');
    return this.http.get(this.config.baseUrl + `/api/DomainData/Cities/${province}`, { headers });
  }

  registerBP(userRegisterData: UserRegisterDataDTO): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + '/api/UserAccount/registerBP', userRegisterData, { headers });
  }

  public updatePreferences(updatePreferences: UpdatePreferencesDTO): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(
      this.config.baseUrl + `/api/preferencerecord/updatepreferences`,
      updatePreferences,
      { headers }
    );
  }

  isActivateUser(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>( this.config.baseUrl + `/api/account/isactivateuser`, payload, { headers });
  }

  redeemCode(redeemCode: RedeemCodeDTO): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/redeem/redeemCode`, redeemCode, { headers });
  }

  public mainmedicalstastlimits(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalstats/mainmedicalstastlimits`,
      payload,
      { headers }
    );
  }

  schedule(scheduleDTO: ScheduleDTO): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/notification/schedule`, scheduleDTO, { headers });
  }

  public preferences(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/preferencerecord/preferences`,
      payload,
      { headers }
    );
  }

  public listfive(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/notification/user/listfive`,
      payload,
      { headers }
    );
  }

  public mainmedicalstatsmultilanguage(individualId: string, birthDay: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalstats/mainmedicalstatsmultilanguage/${birthDay}`,
      payload,
      { headers }
    );
  }

  secondaryprofiles(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get(this.config.baseUrl + `/api/user/secondaryprofiles`, { headers });
  }

  public notificationUserNewcount(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/notification/user/new/count`,
      payload,
      { headers }
    );
  }
  cardsvisibility(data: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/card/cardsvisibility`, data, { headers });
  }

  getCompanyConfiguration(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get(this.config.baseUrl + `/api/BackOffice/getCompanyConfiguration/${environment.hostname}`, { headers });
  }

  getCompanyConfigurationPreview(companyCode: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get(this.config.baseUrl + `/api/BackOffice/getCompanyConfigurationPreview/${companyCode}`, { headers });
  }

  getSsoConfiguration(): Observable<SsoConfiguration> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<SsoConfiguration>(this.config.baseUrl + `/api/Utility/getConfiguration`, { headers })
  }

  callMeBackTravel(body: any): Observable<CallMeBackTravel> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<CallMeBackTravel>(this.config.baseUrl + `/api/consult/callmebacktravel`, body, { headers });
  }

  getAccountdDataNoConnectionValidations(individualId: string): Observable<any> {
    const headers = this.getHeaders();
    const body = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/user/accountdataNoConnectionValidations`,
      body,
      { headers }
    );
  }

  public getAppointments(payload: AppointmentsPayload): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/Consult/userstory`, payload, { headers });
  }

  /**
   * Update account of the user
   * @param {object} profileData
   * @returns {Observable<any>}
   */
  updateAccount(payload: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.put(this.config.baseUrl + `/api/user/updateaccount`, payload, { headers });
  }

  /**
   * Update account of the user
   * @param {IChangePassword} passwordPayload
   * @returns {Observable<any>}
   */
  changePassword(passwordPayload: IChangePassword): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/Account/changepassword`, passwordPayload, { headers });
  }

  public markAllAsRead(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.put<any>(
      this.config.baseUrl + `/api/notification/markread/user`,
      payload,
      { headers }
    );
  }

  markAsRead(notification: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.put<any>(this.config.baseUrl + `/api/notification/markread`, notification, { headers });
  }

  public getNotifications(individualId: string, pageSize: number, pageIndex: number): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/notification/user/${pageSize}/${pageIndex}`,
      payload,
      { headers }
    );
  }

  deleteNotification(id: number): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.delete<any>(this.config.baseUrl + `/api/notification/${id}`, { headers });
  }


  createMedicalProfile(userData: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/user/createmedicalprofile`, userData, { headers });
  }

  createAdultMedicalProfile(userData: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/User/guest/createAdultEAGuest`, userData, { headers });
  }

  createTutorConnection(firstId: string, secondId: string): Observable<any> {
    const headers = this.getHeaders();
    const body = { firstId, secondId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/user/createtutorconnectionext`,
      body,
      { headers }
    );
  }

  updateMedicalStats(type: MedicalMisurationType, body: any): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/medicalstats/${type}`, body, { headers });
  }

  public getDocuments(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(this.config.baseUrl + `/api/MedicalRecord/documents/1/25`, payload, { headers });
  }

  getConfig() {
    return this.http.get<any>(`/config/config.json`);
  }

  uploadDocuments(body: any): Observable<any> {
    return this.http.post<any>(this.config.baseUrl + `/api/MedicalRecord/uploadDocument`, body);
  }

  public deleteUser(connectionId: string, companyId: string) {
    const headers = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/user/disconnect/${connectionId}/${companyId}`, { headers });
  }

  public createSessionWithLanguage(individualId: string) {
    const language = 'it';
    const headers = this.getHeaders();
    return this.http.post<any>(
      this.config.baseUrl + `/api/Triage/createSessionWithLanguage/${language}`,
      { individualId },
      { headers }
    );
  }

  public systemExpert(sessionId: string) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/triage/systemexpert/${sessionId}`, { headers });
  }

  public updateSession(sessionId: string, body: any) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.put<any>(this.config.baseUrl + `/api/triage/sessionupdate/${sessionId}`, body, { headers });
  }

  public medicalProfile(individualId: string): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post<any>(
      this.config.baseUrl + `/api/user/medicalprofile`,
      { individualId },
      { headers }
    );
  }

  public userMedicalProfileData(individualId: string): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post<any>(
      this.config.baseUrl + `/api/user/medicalprofiledata`,
      { individualId },
      { headers }
    );
  }

  public symptomsByZone(sessionId: string, zone: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/triage/symptomsbyzone/${sessionId}/${zone}`, { headers });
  }

  public getDiagnosis(sessionId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/triage/diagnosis/${sessionId}`, { headers });
  }

  public getReportDetails(sessionId: string | undefined): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/Triage/checkupreport/${sessionId}`, { headers });
  }

  public getEmergencyNumber(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/triage/internationalemergencynumbers`, { headers });
  }

  public getUserStory(individualId: string, consultType: number, pageIndex: number, pageSize: number, fromDate: string, toDate: string, language: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      individualId,
      consultType,
      pageIndex,
      pageSize,
      fromDate,
      toDate,
      language,
    };
    return this.http.post(this.config.baseUrl + `/api/Consult/userstory`, payload, { headers });
  }

  public getUserStoryActual(individualId: string, consultType: number, language: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      individualId,
      consultType,
      language,
    };
    return this.http.post(this.config.baseUrl + `/api/Consult/userstoryactual`, payload, { headers });
  }

  public getUserStoryPassed(individualId: string, consultType: number, language: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      individualId,
      consultType,
      language,
    };
    return this.http.post(this.config.baseUrl + `/api/Consult/userstorypassed`, payload, { headers });
  }

  public downloadAllDocuments(individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalrecord/medicalpassport/ita/GEN/true`,
      payload,
      { headers }
    );
  }

  public quotation(sodType: number, trackingCode: string = 'TEST'): Observable<any> {
    /* TODO: il trackingCode attualmente va passato sempre TEST */
    const headers = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/OnDemandService/quotation?sodType=${sodType}&trackingCode=${trackingCode}`, { headers });
  }

  public initializePayment(paymentConf: IInitializePayment): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/OnDemandService/initializepurchase`, paymentConf, { headers });
  }

  public getCounters(individualId: string): Observable<any> {
    const headers = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/pharmap/getCounters`,
      payload,
      { headers }
    );
  }

  public openPharmap(payload: any): Observable<any> {
    const headers = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/pharmap/openPharmap`, payload, { headers });
  }

  public openDossier(accountIndividualId: string, individualId: string): Observable<any> {
    const headers = this.getHeaders();
    const payload = { accountIndividualId, individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/network/opendossier`,
      payload,
      { headers }
    );
  }

  public getPaymentsHistory() {
    const headers = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/OnDemandService/list`, { headers });
  }

  public viewDocument(docName: string, individualId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post(this.config.baseUrl + `/api/MedicalRecord/previewDocument/${docName}`, { individualId: individualId }, { headers, responseType: 'blob' });
  }

  public deleteMedicalStats(type: string, individualId: string, id: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/medicalstats/${type}/${id}`,
      payload,
      { headers }
    );
  }

  public getExternalUserIdByCode(code: string, companyId: string) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/oauth/externaluserid/${companyId}/${code}`, { headers });
  }

  public getAuthToken(externalUserId: string, companyId: string) {
    const headers: HttpHeaders = this.getHeaders();
    const body = {
      externalUserId: externalUserId,
      companyId: companyId,
    };
    return this.http.post<any>(this.config.baseUrl + `/api/oauth/loginbp`, body, { headers });
  }

  public getUserData(token: string, individualId: string) {
    let headers = this.getHeaders();
    headers = headers.append('Authorization', `Bearer ${token}`);
    const body = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/useraccount/AccountData`,
      body,
      { headers }
    );
  }

  public deleteAppointment(individualId: string, dossierCode: string, missionId: string, type: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const body = { individualId, dossierCode, missionId };
    switch (type) {
      case 'SpecialistPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deletephonespecialistByIndividualId`, body, { headers });
      case 'SpecialistVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteVideospecialistByIndividualId`, body, { headers });
      case 'GenericTelevisitVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteVideogenericTeleVisitByIndividualId`, body, { headers });
      case 'SpecialistTelevisitVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteVideospecialistTeleVisitByIndividualId
`, body, { headers });
      case 'VeterinarianPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deletePhoneveterinarianByIndividualId`, body, { headers });
      case 'VeterinarianVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deletevideoveterinarianByIndividualId`, body, { headers });
      case 'Psicologo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteTreatmentPathPsiTelevisitFuByIndividualId`, body, { headers });
      case 'Nutrizionista':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteTreatmentPathNutTelevisitFuByIndividualId`, body, { headers });
      case 'PsychologistVideo':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteVideopsychologistByIndividualId`, body, { headers });
      case 'TreatmentPathNutTelevisit':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteTreatmentPathNutTelevisitByIndividualId`, body, { headers });
      case 'TreatmentPathPsiTelevisit':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteTreatmentPathPsiTelevisitByIndividualId`, body, { headers });
      case 'PsychologistPhone':
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deletePhonepsychologistByIndividualId`, body, { headers });
      default:
        return this.http.post<any>(this.config.baseUrl + `/api/Consult/deleteVideospecialistByIndividualId`, body, { headers });
    }
  }

  public modifyAppointment(body: any, individualId: string, type: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    body.individualId = individualId;
    switch (type) {
      case 'GenericPhone':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/phonegenericByIndividualId`, body, { headers });
      case 'GenericVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videogenericByIndividualId`, body, { headers });
      case 'SpecialistPhone':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/phonespecialistByIndividualId`, body, { headers });
      case 'SpecialistVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videospecialistByIndividualId`, body, { headers });
      case 'GenericTelevisitVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videogenericTeleVisitByIndividualId`, body, { headers });
      case 'SpecialistTelevisitVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videospecialistTeleVisitByIndividualId`, body, { headers });
      case 'VeterinarianPhone':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/phoneveterinarianByIndividualId`, body, { headers });
      case 'VeterinarianVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videoveterinarianByIndividualId`, body, { headers });
      case 'Psicologo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/treatmentPathPsiTelevisitFuByIndividualId`, body, { headers });
      case 'Nutrizionista':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/treatmentPathNutTelevisitFuByIndividualId`, body, { headers });
      case 'PsychologistVideo':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videopsychologistByIndividualId`, body, { headers });
      case 'TreatmentPathNutTelevisit':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/treatmentPathNutTelevisitByIndividualId`, body, { headers });
      case 'TreatmentPathPsiTelevisit':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/treatmentPathPsiTelevisitByIndividualId`, body, { headers });
      case 'PsychologistPhone':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/phonepsychologistByIndividualId`, body, { headers });
      case 'CareManager':
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/caremanager`, body, { headers });
      default:
        return this.http.put<any>(this.config.baseUrl + `/api/Consult/videospecialistByIndividualId`, body, { headers });
    }
  }

  public getKinship(): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/consult/kinship/`, { headers });
  }

  public getSurveyValues(type: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    switch (type) {
      case 'TreatmentPathPsiTelevisitFu':
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/TreatmentPathPsyFu/it`, { headers });
      case 'TreatmentPathNutTelevisitFu':
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/TreatmentPathNutFu/it`, { headers });
      case 'TreatmentPathNutTelevisit':
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/TreatmentPathNut/it`, { headers });
      case 'TreatmentPathPsiTelevisit':
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/TreatmentPathPsy/it`, { headers });
      case 'CareManager':
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/CareManager/it`, { headers });
      default:
        return this.http.get<any>(this.config.baseUrl + `/api/Survey/surveyValuesTreatmentPath/CareManager/it`, { headers });
    }
  }

  public downloadMedicalData(individualId: string) {
    const lang = 'ita';
    const source = 'EA';
    const headers: HttpHeaders = this.getHeaders();
    const payload = { individualId };
    return this.http.post<any>(
      this.config.baseUrl + `/api/expiredaccount/downloadprivatemedicaldata/${lang}/${source}`,
      payload,
      { headers }
    );
  }

  public getConsultRequestDetail(id: string) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.post<any>(this.config.baseUrl + `/api/Consult/consultRequestDetail/${id}`, { headers });
  }

  public getGoogleExistUser(googleId: string, companyId: string) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/Google/Google/GoogleUserExistsInEA/${googleId}/${companyId}`, { headers });
  }

  public getGoogleSSO(googleId: string, companyId: string) {
    const headers: HttpHeaders = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/Google/Google/SSO/${googleId}/${companyId}`, { headers });
  }

  public getAllMedicalRecordByType(type: string) {
    const headers: HttpHeaders = this.getHeaders();
    const individualId = JSON.parse(localStorage.getItem('userData')!).individualId;
    const body = {
      individualId: individualId,
    };
    switch (type) {
      case 'pathology':
      case 'vaccination':
        return this.http.post<any>(this.config.baseUrl + `/api/MedicalRecord/${type}/false/0/25`, body, { headers });
      default:
        return this.http.post<any>(this.config.baseUrl + `/api/MedicalRecord/${type}/0/25`, body, { headers });
    }
  }

  public getPaymentDetails(rateNumber: string) {
    const headers = this.getHeaders();
    return this.http.get<any>(this.config.baseUrl + `/api/OnDemandService/detail?rateNumber=${rateNumber}`, { headers });
  }

  public updateEmail(mainIndividualId: string, minorIndividualId: string, email: string): Observable<any> {
    const headers = this.getHeaders();
    const body = { mainIndividualId, minorIndividualId, email };
    return this.http.post<any>(
      this.config.baseUrl + `/api/User/switchAccountext`,
      body,
      { headers }
    );
  }


  public sendFeedback(payload: any) {
    const headers = this.getHeaders();
    return this.http.put<any>(this.config.baseUrl + `/api/ConsultRequestSatisfactionSurvey/saveTelevisitSurveyByIndividualId`, payload, { headers });
  }

  getContentfulEntryIds(landingPageId: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      landindPageId: landingPageId
    };
    return this.http.post<any>(
      this.config.baseUrl + `/api/Contentful/Entry`,
      payload,
      { headers }
    );
  }

  getContentfulEntry(sysId: string, locale: string): Observable<any> {
    const headers: HttpHeaders = this.getHeaders();
    const payload = {
      sysId: sysId,
      locale: locale
    };
    return this.http.post<any>(
      this.config.baseUrl + `/api/Contentful/Entries`,
      payload,
      { headers }
    );
  }

  GetRebrandBanner() {
    const headers: HttpHeaders = this.getHeaders();
    return this.config.getConfig().pipe(
      switchMap((config) => {
        const url = `${config?.eaContenful}/api/Contentful/GetRebrandBanner`;
        return this.http.get<any>(url, { headers });
      })
    );
  }
}
